// src/pages/CreateCharacter.js
// Make a character: AI-assisted persona draft → optional avatar → publish.
// Also lists the user's own bots with delete (backend allows delete within 24h).
//   POST /generate-ai-character · POST /images/generate-bot · POST /bots
//   GET /bots/my-created · DELETE /bots/{id}

import { useEffect, useState } from "react";
import Avatar from "../components/Avatar";
import { bots as botsApi, images } from "../api";

const CATEGORIES = ["romance", "anime", "gaming", "roleplay", "helper", "celebrity", "fictional", "other"];

export default function CreateCharacter({ onOpenChat }) {
  const [idea, setIdea] = useState("");
  const [drafting, setDrafting] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [personality, setPersonality] = useState("");
  const [scenario, setScenario] = useState("");
  const [firstMessage, setFirstMessage] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [category, setCategory] = useState("roleplay");
  const [tags, setTags] = useState("");
  const [isPublic, setIsPublic] = useState(true);

  const [avatarB64, setAvatarB64] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [mine, setMine] = useState([]);

  const loadMine = () => botsApi.myCreated().then(setMine).catch(() => {});
  useEffect(() => {
    loadMine();
  }, []);

  const draft = async () => {
    if (!idea.trim() || drafting) return;
    setErr("");
    setDrafting(true);
    try {
      const c = await botsApi.aiDraft(idea, name);
      setName(c.name || name);
      setDescription(c.description || "");
      // backend returns `personality` (+ `story`); the bot field is personality_prompt
      setPersonality([c.personality, c.story].filter(Boolean).join("\n\n"));
      setScenario(c.scenario || "");
      setFirstMessage(c.first_message || "");
      setImagePrompt(c.image_prompt || "");
    } catch (e) {
      setErr(e.message);
    } finally {
      setDrafting(false);
    }
  };

  const makeAvatar = async () => {
    const p = imagePrompt.trim() || description.trim() || name.trim();
    if (!p || avatarBusy) return;
    setErr("");
    setAvatarBusy(true);
    try {
      const res = await images.generateBot(p);
      const b64 = res.image_base64 || res.images?.[0]?.image_base64 || null;
      const url = res.image_url || res.images?.[0]?.image_url || (b64 ? `data:image/png;base64,${b64}` : null);
      setAvatarB64(b64);
      setAvatarPreview(url);
    } catch (e) {
      setErr(e.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  const create = async () => {
    if (!name.trim() || !personality.trim() || creating) {
      if (!name.trim() || !personality.trim()) setErr("Name and personality are required.");
      return;
    }
    setErr("");
    setMsg("");
    setCreating(true);
    try {
      const bot = await botsApi.create({
        name: name.trim(),
        description: description.trim() || "An AI companion.",
        personality_prompt: personality.trim(),
        scenario: scenario.trim() || null,
        first_message: firstMessage.trim() || null,
        short_personality: personality.trim().slice(0, 400),
        avatar_base64: avatarB64,
        category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        is_public: isPublic,
      });
      setMsg(`✓ "${bot.name}" created.`);
      // reset the form
      setIdea("");
      setName("");
      setDescription("");
      setPersonality("");
      setScenario("");
      setFirstMessage("");
      setImagePrompt("");
      setAvatarB64(null);
      setAvatarPreview(null);
      loadMine();
    } catch (e) {
      setErr(e.detail?.message || e.message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (bot) => {
    if (!window.confirm(`Delete "${bot.name}"? (only possible within 24h of creation)`)) return;
    try {
      await botsApi.remove(bot.id);
      setMine((xs) => xs.filter((b) => b.id !== bot.id));
    } catch (e) {
      alert(e.detail?.message || e.message);
    }
  };

  return (
    <div className="page">
      <div className="page__head">
        <h1>Create a character</h1>
        <p className="page__sub">Describe an idea, let AI draft the persona, tweak, and publish.</p>
      </div>

      <div className="studio">
        <div className="studio__form">
          <label className="field">
            <span>Your idea</span>
            <textarea rows={2} value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="A shy cyberpunk hacker who secretly writes poetry…" />
          </label>
          <button className="btn btn--ghost btn--block" onClick={draft} disabled={drafting || !idea.trim()}>
            {drafting ? "Drafting with AI…" : "✨ Generate with AI"}
          </button>

          <label className="field">
            <span>Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Aiko" />
          </label>
          <label className="field">
            <span>Description (short)</span>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One-line tagline shown on the card." />
          </label>
          <label className="field">
            <span>Personality &amp; backstory</span>
            <textarea rows={5} value={personality} onChange={(e) => setPersonality(e.target.value)} placeholder="Traits, speaking style, quirks, backstory…" />
          </label>
          <label className="field">
            <span>Scenario (the situation the chat starts in)</span>
            <textarea rows={3} value={scenario} onChange={(e) => setScenario(e.target.value)} placeholder="Present setting and where things are heading — drives every reply." />
          </label>
          <label className="field">
            <span>First message</span>
            <textarea rows={2} value={firstMessage} onChange={(e) => setFirstMessage(e.target.value)} placeholder="How they greet a new user." />
          </label>

          <div className="field">
            <span>Category</span>
            <select className="sort" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: "100%" }}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
          <label className="field">
            <span>Tags (comma-separated)</span>
            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="anime, sci-fi, tsundere" />
          </label>

          <button className={`toggle ${isPublic ? "toggle--on" : ""}`} onClick={() => setIsPublic((v) => !v)}>
            <span>🌐 Public (others can discover &amp; chat)</span>
            <span className="toggle__knob" />
          </button>

          {err && <div className="form-error">{err}</div>}
          {msg && <div className="form-note">{msg}</div>}

          <button className="btn btn--primary btn--block" onClick={create} disabled={creating || !name.trim() || !personality.trim()}>
            {creating ? "Creating…" : "Create character"}
          </button>
        </div>

        <div className="studio__out">
          <span className="studio__outlabel">Avatar</span>
          <div className="cc-avatar">
            {avatarPreview ? (
              <img src={avatarPreview} alt="avatar preview" />
            ) : (
              <Avatar name={name || "?"} size={120} radius="20px" />
            )}
          </div>
          <label className="field" style={{ marginTop: 12 }}>
            <span>Image prompt</span>
            <textarea rows={3} value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} placeholder="beautiful anime character portrait, cinematic lighting…" />
          </label>
          <button className="btn btn--ghost btn--block" onClick={makeAvatar} disabled={avatarBusy}>
            {avatarBusy ? "Generating…" : "🎨 Generate avatar (free)"}
          </button>
        </div>
      </div>

      {mine.length > 0 && (
        <div className="cc-mine">
          <h3 className="history__title">Your characters</h3>
          <div className="grid">
            {mine.map((b) => (
              <div key={b.id} className="card">
                <button className="card__media" onClick={() => onOpenChat?.(b)}>
                  <Avatar name={b.name} url={b.avatar_url} size={80} radius="20px" />
                </button>
                <div className="card__body">
                  <div className="card__title">{b.name}</div>
                  <div className="card__desc">{b.description}</div>
                  <div className="mcard__row" style={{ marginTop: 10 }}>
                    <span className="mcard__stat">{b.is_public ? "Public" : "Private"}</span>
                    <button className="mcard__stat" onClick={() => remove(b)} title="Delete">🗑</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
